import { api, unwrap, type APIResponse } from "./client";

export interface UserMessage {
  message_id: number;
  thread_id: number;
  sender_usr_id: number;
  sender_username: string;
  sender_name: string;
  recipient_usr_id: number;
  recipient_username: string;
  recipient_name: string;
  subject: string;
  body: string;
  priority: "normal" | "important" | "urgent";
  category: "general" | "playbook" | "performance" | "billing" | "system";
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  thread_count: number;
}

export interface UnreadSummary {
  unread_count: number;
  urgent_unread_count: number;
  latest_id: number | null;
  latest_subject: string | null;
  latest_sender: string | null;
  latest_priority: string | null;
  latest_created_at: string | null;
}

export interface RecipientOption {
  usr_id: number;
  username: string;
  display_name: string;
  email: string | null;
  is_admin: boolean;
}

export interface SendMessageInput {
  recipient_usr_id?: number | null;
  broadcast_all?: boolean;
  thread_id?: number | null;
  subject: string;
  body: string;
  priority?: "normal" | "important" | "urgent";
  category?: "general" | "playbook" | "performance" | "billing" | "system";
}

export async function getUnreadSummary(): Promise<UnreadSummary> {
  const resp = await api.get<APIResponse<UnreadSummary>>("/messages/unread-count");
  return unwrap(resp.data);
}

export async function listMessages(
  folder: "inbox" | "unread" | "sent" | "all" = "inbox",
  usrFilter?: number | null,
): Promise<UserMessage[]> {
  const params: Record<string, string | number> = { folder };
  if (usrFilter) params.usr_filter = usrFilter;
  const resp = await api.get<APIResponse<UserMessage[]>>("/messages/", { params });
  return unwrap(resp.data);
}

export async function getMessageThread(threadId: number): Promise<UserMessage[]> {
  const resp = await api.get<APIResponse<UserMessage[]>>(`/messages/thread/${threadId}`);
  return unwrap(resp.data);
}

export async function listRecipients(): Promise<RecipientOption[]> {
  const resp = await api.get<APIResponse<RecipientOption[]>>("/messages/recipients");
  return unwrap(resp.data);
}

export async function sendMessage(body: SendMessageInput): Promise<{ message_id?: number; sent_count?: number }> {
  const resp = await api.post<APIResponse<{ message_id?: number; sent_count?: number }>>("/messages/", body);
  return unwrap(resp.data);
}

export async function markMessageRead(messageId: number): Promise<void> {
  await api.post(`/messages/${messageId}/read`);
}

export async function markAllMessagesRead(): Promise<void> {
  await api.post("/messages/read-all");
}

export async function archiveMessage(messageId: number): Promise<void> {
  await api.delete(`/messages/${messageId}`);
}
